/**
 * Formats a price according to the given locale with EUR currency
 * @param {object} i18n - Internationalization object containing language preference
 * @param {number} price - The price value to format
 * @returns {string} - Formatted price string with currency symbol
 */
exports.formatPrice = (i18n,price) => {
        // Step 1: Format the price using Intl.NumberFormat with the locale and currency
    const formattedPrice = new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'EUR',
        currencyDisplay: 'symbol',  // Removes space between symbol and number
    }).format(price);

    // Step 2: Remove the space between the currency symbol and the number if it exists
    const noSpacePrice = formattedPrice.replace(/\s?(€|₤|₹|¥|₣)/, '$1');  // Adjust for common currency symbols

    return noSpacePrice;
}

/**
 * Formats a number to local number
 * @param {*} i18n 
 * @param {*} number 
 * @returns 
 */
exports.formatNumber = (i18n,number) => {
    return new Intl.NumberFormat(i18n.language, {}).format(number);
}
/**
 * Formats input to float
 * @param {*} input 
 * @returns 
 */
exports.convertInputToFloat = (input) => {
    return parseFloat(input.indexOf(",") > -1 ? input.replace(",", ".") : input);
}

/**
 * Removes unnecessary trailing zeros from a number
 * @param {number|string} input - The number to format
 * @returns {string} - Formatted number without trailing zeros
 *                     (e.g., "1.20" becomes "1.2", "1.00" becomes "1")
 */
exports.removeTrailingZeros = (input) => {
    // Format amount to remove trailing zeros and only show necessary decimals
    const num = Number(input);
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Checks if a given date is in the future, comparing dates only (ignoring time)
 * @param {string} date - Date string in YYYY-MM-DD format
 * @returns {boolean} - Returns true if the date is in the future, false otherwise
 */
exports.isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    return compareDate > today;
}


exports.errorMapWithArrayProcessor = (errors,toMatchTo) => {
    const fieldErrors = {};
    const generalErrors = {};

    Object.keys(errors).forEach((errorKey) => {
        const match = errorKey.match(toMatchTo);
        if (match) {
            const index = parseInt(match[1], 10); 
            const field = match[2]; 

            if (!fieldErrors[index]) {
            fieldErrors[index] = {};
            }
            
            fieldErrors[index][field] = errors[errorKey];
        } else {
            generalErrors[errorKey] = errors[errorKey];
        }
    });

    return { fieldErrors, generalErrors };
};

exports.cleanQrCode = (input) => {
    // https://qrplanet.com/help/article/example-of-a-batch-or-lot-number-10-according-to-gs1
    // GS1 barcodes pack multiple pieces of data into one scan. Each piece is prefixed
    // with an "Application Identifier" (AI) — a 2-4 digit code that says what follows.
    //
    // Example scan: "0101234567890128" + GS + "10ABC123"
    //   AI 01 = product code (GTIN), always 14 digits
    //   AI 10 = batch/lot number, variable length
    //   GS = invisible separator character (ASCII 29) between pieces
    //
    // This function strips out the batch/lot number (AI 10) so we're left with
    // just the product identifier. But we have to be careful: a plain barcode
    // like "1012345" also starts with "10" — we should NOT strip those.

    // Step 1: Normalize the hidden GS separator — scanners sometimes encode it as %1D
    const GS = String.fromCharCode(29);
    let normalized = input.replace(/%1D/g, GS);

    // Step 2: Figure out if this is a GS1 barcode or just a plain barcode.
    // Two clues: it contains a GS separator, or it starts with a known AI code.
    const hasGsSeparator = normalized.includes(GS);
    const startsWithKnownAI = /^(01|02|00|10|11|17|21|30|37|310|320|330|390|392|410|421|8004|8005|90|91|92|93|94|95|96|97|98|99)/.test(normalized);

    if (!hasGsSeparator && !startsWithKnownAI) {
        // Not a GS1 barcode — return as-is
        return normalized;
    }

    // Step 3: Some GS1 barcodes don't have GS separators between pieces.
    // For those, we can split them manually because some AIs have a fixed data length.
    // Example: "010123456789012810ABC123" — AI 01 is always 16 chars total (2 for "01" + 14 digits),
    // so we know the batch part starts at position 16.
    if (!hasGsSeparator) {
        const fixedLengthAIs = {
            '00': 20,  // SSCC: 2 char AI + 18 digits
            '01': 16,  // GTIN: 2 char AI + 14 digits
            '02': 16,  // GTIN of contained items: 2 char AI + 14 digits
            '11': 8,   // Production date: 2 char AI + 6 digits (YYMMDD)
            '17': 8,   // Expiry date: 2 char AI + 6 digits (YYMMDD)
        };

        const aiPrefix = normalized.substring(0, 2);
        const fixedLen = fixedLengthAIs[aiPrefix];

        if (fixedLen && normalized.length > fixedLen) {
            // Insert a GS separator so the split below works
            normalized = normalized.substring(0, fixedLen) + GS + normalized.substring(fixedLen);
        }
    }

    // Step 4: Split into pieces and remove any AI 10 (batch/lot) pieces
    const chunks = normalized.split(GS);
    const filtered = chunks.filter(chunk => !chunk.startsWith('10'));

    // Step 5: If removing AI 10 left us with nothing, this was probably a plain
    // barcode that just happened to start with "10" — return the original input
    if (filtered.length === 0) {
        return input.replace(/%1D/g, '');
    }

    // Remove any leftover GS separators and return
    return filtered.join('');
  }
// The pickup method of a pickup order is stored as a fixed suffix at the end of the order remark
const pickupMethodRemarkLabels = { boxes: "Losse dozen", pallet: "Pallet" };
const pickupMethodRemarkPattern = /(?: \| )?Afhaalmethode: (Losse dozen|Pallet)(?: \(max\. hoogte (\d+) cm\))?$/;

/**
 * Splits an order remark into the free text and the pickup method stored at its end
 * @param {string} remark - The full order remark
 * @returns {{remark: string, pickupMethod: string|null, maxHeight: number|null}}
 */
exports.parsePickupMethodRemark = (remark) => {
    const fullRemark = remark ?? "";
    const match = fullRemark.match(pickupMethodRemarkPattern);
    if (!match) {
        return { remark: fullRemark, pickupMethod: null, maxHeight: null };
    }

    return {
        remark: fullRemark.slice(0, match.index),
        pickupMethod: match[1] === pickupMethodRemarkLabels.pallet ? "pallet" : "boxes",
        maxHeight: match[2] ? parseInt(match[2]) : null,
    };
}

/**
 * Builds the order remark from the free text and the pickup method
 * @param {string} remark - The free text of the remark
 * @param {string|null} pickupMethod - "boxes", "pallet" or empty for no pickup method
 * @param {number|null} maxHeight - Maximum pallet height in cm, only used for "pallet"
 * @returns {string}
 */
exports.buildPickupMethodRemark = (remark, pickupMethod, maxHeight) => {
    const freeText = remark ?? "";
    const label = pickupMethodRemarkLabels[pickupMethod];
    if (!label) {
        return freeText;
    }

    const height = pickupMethod === "pallet" && maxHeight > 0 ? ` (max. hoogte ${Math.round(maxHeight)} cm)` : "";
    return `${freeText}${freeText ? " | " : ""}Afhaalmethode: ${label}${height}`;
}
